"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useIsMobile } from "@/hooks";
import { cn } from "@/lib/utils";
import { createClient } from "@/lib/supabase/client";

// project.md: "Ana Sayfa", "Favoriler", "Evini Listele" ve "Uygulamayı İndir"
// İkonlar /public altındaki SVG'lerden CSS mask ile renklendirilir,
// böylece mobil uygulama ile birebir aynı görsel dilde kalır.
const navItems = [
  {
    href: "/",
    label: "Ana Sayfa",
    icon: "/anasayfa.svg",
    requiresAuth: false,
  },
  {
    href: "/favorites",
    label: "Favoriler",
    icon: "/favoriler.svg",
    requiresAuth: false,
  },
  {
    href: "/for-hosts",
    label: "Evini Listele",
    icon: "/ekle.svg",
    requiresAuth: false, // for-hosts sayfası herkese açık, ilan ekleme sayfası korumalı
  },
  {
    href: "/profile",
    label: "Profil",
    icon: "/profil.svg",
    requiresAuth: true,
  },
];

// Hangi route prefix'lerinde bottom nav HİÇ gösterilmesin
const HIDDEN_ROUTE_PREFIXES = ["/listings/"];

export function MobileBottomNav() {
  const isMobile = useIsMobile();
  const pathname = usePathname();
  const router = useRouter();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hiddenByScroll, setHiddenByScroll] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const supabase = createClient();
    if (!supabase) return;

    // Mevcut kullanıcıyı kontrol et
    supabase.auth.getUser().then(({ data: { user } }) => {
      setIsAuthenticated(!!user);
    });

    // Auth state değişikliklerini dinle
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session?.user);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Scroll-hide davranışı: aşağı kaydırınca gizle, yukarı kaydırınca göster
  useEffect(() => {
    if (!isMobile) return;
    lastScrollY.current = window.scrollY;

    const SCROLL_DELTA_THRESHOLD = 8; // küçük kıpırdamalarda titremesin
    let ticking = false;

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const currentY = window.scrollY;
        const delta = currentY - lastScrollY.current;

        // Sayfanın en üstündeysen her zaman göster
        if (currentY < 20) {
          setHiddenByScroll(false);
        } else if (Math.abs(delta) > SCROLL_DELTA_THRESHOLD) {
          // Aşağı kaydırma → gizle, yukarı kaydırma → göster
          setHiddenByScroll(delta > 0);
          lastScrollY.current = currentY;
        }
        ticking = false;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile]);

  // Belirli sayfalarda HİÇ gösterme (örn. ilan detay)
  const isHiddenRoute = HIDDEN_ROUTE_PREFIXES.some(
    (prefix) =>
      pathname.startsWith(prefix) && pathname !== "/listings" && pathname !== "/listings/new"
  );

  if (!isMobile || isHiddenRoute) return null;

  const handleNavClick = (item: (typeof navItems)[0], e: React.MouseEvent) => {
    if (item.requiresAuth && !isAuthenticated) {
      e.preventDefault();
      router.push("/auth");
    }
  };

  return (
    <nav
      className={cn(
        "border-border pb-safe fixed right-0 bottom-0 left-0 z-50 border-t bg-white",
        "transition-transform duration-300 ease-out will-change-transform",
        hiddenByScroll ? "translate-y-full" : "translate-y-0"
      )}
    >
      <div className="flex h-16 items-center justify-around">
        {navItems.map((item) => {
          const isActive = pathname === item.href;

          return (
            <Link
              key={item.href}
              href={item.href}
              onClick={(e) => handleNavClick(item, e)}
              className={cn(
                "flex flex-col items-center gap-1 px-3 py-2 transition-colors",
                isActive ? "text-primary" : "text-muted-foreground"
              )}
              aria-label={item.label}
              aria-current={isActive ? "page" : undefined}
            >
              <span
                aria-hidden
                className="block h-6 w-6 bg-current"
                style={{
                  WebkitMaskImage: `url(${item.icon})`,
                  maskImage: `url(${item.icon})`,
                  WebkitMaskRepeat: "no-repeat",
                  maskRepeat: "no-repeat",
                  WebkitMaskPosition: "center",
                  maskPosition: "center",
                  WebkitMaskSize: "contain",
                  maskSize: "contain",
                }}
              />
              <span className="text-[10px] font-medium">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
